// Topologie ostrova (stejná pro každou partii): 19 polí, 54 rohů, 72 hran, 9 míst pro stanice.
// Pole jsou šestiúhelníky „špičkou nahoru“ v axiálních souřadnicích s poloměrem 2.
// ID jsou indexy do polí níže, seřazené podle polohy shora dolů a zleva doprava,
// takže „další“ položka v seznamu i na mapě postupuje přirozeně.

export interface Hex {
  /** Axiální souřadnice. */
  q: number;
  r: number;
  /** Střed pro kreslení (jednotka = poloměr šestiúhelníku). */
  x: number;
  y: number;
  /** Lidské označení: řada A až E a pořadí v řadě, např. „C3“. */
  label: string;
  corners: number[];
  edges: number[];
  /** Sousední pole (pro pravidlo „6 a 8 ne vedle sebe“). */
  neighbors: number[];
}

export interface Corner {
  x: number;
  y: number;
  /** Cik-cak řada 0 až 5 a pořadí v ní (pro procházení šipkami). */
  row: number;
  col: number;
  hexes: number[];
  corners: number[];
  edges: number[];
}

export interface Edge {
  x: number;
  y: number;
  corners: [number, number];
  hexes: number[];
  /** Svislá hrana (spojuje dvě cik-cak řady) nebo šikmá (vede po řadě). */
  vertical: boolean;
}

export interface Topology {
  hexes: Hex[];
  corners: Corner[];
  edges: Edge[];
  /** Pobřežní hrany v pořadí po obvodu (po směru hodinových ručiček od vrcholu). */
  coast: number[];
  /** 9 hran, kde mohou stát obchodní stanice. */
  portEdges: number[];
}

const SQ3 = Math.sqrt(3);
const round = (v: number) => Math.round(v * 1000) / 1000;
const key = (x: number, y: number) => `${round(x)},${round(y)}`;

function build(): Topology {
  // Pole v pořadí čtení: řada r = -2..2, v řadě q vzestupně.
  const raw: { q: number; r: number }[] = [];
  for (let r = -2; r <= 2; r++) for (let q = -2; q <= 2; q++) if (Math.abs(q + r) <= 2) raw.push({ q, r });

  const cornerPts = new Map<string, { x: number; y: number }>();
  const hexCornerKeys: string[][] = [];
  for (const { q, r } of raw) {
    const cx = SQ3 * (q + r / 2);
    const cy = 1.5 * r;
    const keys: string[] = [];
    for (let i = 0; i < 6; i++) {
      // i = 0 je horní špička, dál po směru hodinových ručiček.
      const a = (Math.PI / 180) * (60 * i - 90);
      const x = round(cx + Math.cos(a));
      const y = round(cy + Math.sin(a));
      const k = key(x, y);
      cornerPts.set(k, { x, y });
      keys.push(k);
    }
    hexCornerKeys.push(keys);
  }

  // Rohy seřazené shora dolů, zleva doprava.
  const cornerKeys = [...cornerPts.keys()].sort((a, b) => {
    const pa = cornerPts.get(a)!;
    const pb = cornerPts.get(b)!;
    return pa.y - pb.y || pa.x - pb.x;
  });
  const cornerIndex = new Map(cornerKeys.map((k, i) => [k, i]));
  const corners: Corner[] = cornerKeys.map((k) => ({ ...cornerPts.get(k)!, row: 0, col: 0, hexes: [], corners: [], edges: [] }));

  // Hrany jako dvojice rohů.
  const edgeMap = new Map<string, { a: number; b: number; hexes: number[] }>();
  const hexes: Hex[] = raw.map(({ q, r }, h) => {
    const ids = hexCornerKeys[h].map((k) => cornerIndex.get(k)!);
    for (const c of ids) corners[c].hexes.push(h);
    for (let i = 0; i < 6; i++) {
      const a = ids[i];
      const b = ids[(i + 1) % 6];
      const k = a < b ? `${a}-${b}` : `${b}-${a}`;
      const e = edgeMap.get(k) ?? { a: Math.min(a, b), b: Math.max(a, b), hexes: [] };
      e.hexes.push(h);
      edgeMap.set(k, e);
    }
    return { q, r, x: round(SQ3 * (q + r / 2)), y: round(1.5 * r), label: "", corners: ids, edges: [], neighbors: [] };
  });

  const edgeList = [...edgeMap.values()].map((e) => {
    const pa = corners[e.a];
    const pb = corners[e.b];
    return { x: round((pa.x + pb.x) / 2), y: round((pa.y + pb.y) / 2), corners: [e.a, e.b] as [number, number], hexes: e.hexes, vertical: Math.abs(pa.x - pb.x) < 1e-6 };
  });
  edgeList.sort((a, b) => a.y - b.y || a.x - b.x);
  const edges: Edge[] = edgeList;
  edges.forEach((e, i) => {
    for (const c of e.corners) corners[c].edges.push(i);
    for (const h of e.hexes) hexes[h].edges.push(i);
  });
  for (const e of edges) {
    const [a, b] = e.corners;
    corners[a].corners.push(b);
    corners[b].corners.push(a);
  }

  // Sousední pole: sdílí hranu.
  for (const e of edges) if (e.hexes.length === 2) {
    const [a, b] = e.hexes;
    hexes[a].neighbors.push(b);
    hexes[b].neighbors.push(a);
  }

  // Lidská označení polí: řady A až E, pořadí od 1.
  const rows = [...new Set(hexes.map((h) => h.r))].sort((a, b) => a - b);
  for (const h of hexes) {
    const row = rows.indexOf(h.r);
    const inRow = hexes.filter((o) => o.r === h.r).sort((a, b) => a.q - b.q);
    h.label = `${"ABCDE"[row]}${inRow.indexOf(h) + 1}`;
  }

  // Cik-cak řady rohů: komponenty grafu jen se šikmými hranami, seřazené shora.
  const rowOf = new Array<number>(corners.length).fill(-1);
  const comps: number[][] = [];
  for (let start = 0; start < corners.length; start++) {
    if (rowOf[start] >= 0) continue;
    const comp: number[] = [];
    const stack = [start];
    rowOf[start] = comps.length;
    while (stack.length) {
      const c = stack.pop()!;
      comp.push(c);
      for (const ei of corners[c].edges) {
        if (edges[ei].vertical) continue;
        const o = edges[ei].corners[0] === c ? edges[ei].corners[1] : edges[ei].corners[0];
        if (rowOf[o] < 0) {
          rowOf[o] = comps.length;
          stack.push(o);
        }
      }
    }
    comps.push(comp);
  }
  const order = comps.map((c, i) => ({ i, y: Math.min(...c.map((k) => corners[k].y)) })).sort((a, b) => a.y - b.y);
  order.forEach(({ i }, row) => {
    const sorted = comps[i].slice().sort((a, b) => corners[a].x - corners[b].x);
    sorted.forEach((c, col) => {
      corners[c].row = row;
      corners[c].col = col;
    });
  });

  // Pobřeží po obvodu (úhel od středu, začíná nahoře, po směru hodinových ručiček).
  const coast = edges
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e.hexes.length === 1)
    .sort((a, b) => angle(a.e) - angle(b.e))
    .map(({ i }) => i);
  // 9 stanic s rozestupy 3, 4, 3, 3, 4, 3, 3, 4, 3 hrany (30 pobřežních hran): žádné dvě nesdílí roh.
  const steps = [0, 3, 7, 10, 13, 17, 20, 23, 27];
  const portEdges = steps.map((s) => coast[s]);

  return { hexes, corners, edges, coast, portEdges };
}

function angle(e: { x: number; y: number }): number {
  // 0 nahoře, roste po směru hodinových ručiček.
  const a = Math.atan2(e.x, -e.y);
  return a < 0 ? a + 2 * Math.PI : a;
}

export const TOPOLOGY: Topology = build();

/** Druhý roh hrany. */
export function otherEnd(edge: number, corner: number): number {
  const [a, b] = TOPOLOGY.edges[edge].corners;
  return a === corner ? b : a;
}

/** Hrana mezi dvěma rohy, nebo -1. */
export function edgeBetween(a: number, b: number): number {
  return TOPOLOGY.corners[a].edges.find((e) => otherEnd(e, a) === b) ?? -1;
}
